import { useEffect, useState, useCallback } from "react";
import {
  FiRefreshCw,
  FiUsers,
  FiInbox,
  FiMail,
  FiTrendingUp,
  FiBarChart2,
  FiPieChart,
  FiArrowUp,
  FiArrowDown,
  FiGlobe,
  FiMonitor,
  FiEye,
  FiSearch,
  FiTarget,
  FiFileText,
} from "@/lib/icons";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CardSkeleton } from "@/components/common";
import { cn } from "@/lib/utils";

type GA4Data = {
  realtime: { activeUsers: number };
  overview: {
    totalUsers: number;
    newUsers: number;
    sessions: number;
    pageviews: number;
    avgSessionDuration: number;
    bounceRate: number;
  };
  overviewPrevious: {
    totalUsers: number;
    newUsers: number;
    sessions: number;
    pageviews: number;
  };
  topPages: { page: string; views: number; users: number }[];
  trafficSources: { source: string; users: number; sessions: number }[];
  deviceBreakdown: { device: string; users: number }[];
  dailySessions: { date: string; sessions: number; users: number }[];
  topCountries: { country: string; users: number }[];
};

type AnalyticsData = {
  overview: {
    totalLeads: number;
    totalCustomers: number;
    totalSubscribers: number;
    conversionRate: number;
    leadsThisMonth: number;
    leadsLastMonth: number;
    customersThisMonth: number;
    subscribersThisMonth: number;
  };
  leadsByStatus: Record<string, number>;
  leadsBySource: Record<string, number>;
  leadsByService: Record<string, number>;
  leadsByReferral: Record<string, number>;
  leadsByMonth: { month: string; count: number }[];
  campaigns: {
    totalSent: number;
    totalOpened: number;
    totalClicked: number;
    openRate: number;
    clickRate: number;
    recentCampaigns: {
      name: string;
      sentAt: string;
      totalSent: number;
      totalOpened: number;
      totalClicked: number;
    }[];
  };
  ga4: GA4Data | null;
  searchConsole: SearchConsoleData | null;
  lastUpdated: string;
};

type SearchConsoleData = {
  overview: {
    totalClicks: number;
    totalImpressions: number;
    avgCTR: number;
    avgPosition: number;
  };
  topQueries: {
    query: string;
    clicks: number;
    impressions: number;
    ctr: number;
    position: number;
  }[];
  topPages: {
    page: string;
    clicks: number;
    impressions: number;
    ctr: number;
    position: number;
  }[];
  dailyData: {
    date: string;
    clicks: number;
    impressions: number;
  }[];
};

const statusLabels: Record<string, string> = {
  new: "New",
  contacted: "Contacted",
  qualified: "Qualified",
  proposal: "Proposal",
  converted: "Converted",
  lost: "Lost",
};

const statusColors: Record<string, string> = {
  new: "bg-blue-100 text-blue-700",
  contacted: "bg-yellow-100 text-yellow-700",
  qualified: "bg-emerald-100 text-emerald-700",
  proposal: "bg-purple-100 text-purple-700",
  converted: "bg-green-100 text-green-700",
  lost: "bg-red-100 text-red-700",
};

const sourceLabels: Record<string, string> = {
  contact_form: "Contact Form",
  calculator: "Calculator",
  landing_page: "Landing Page",
};

const serviceLabels: Record<string, string> = {
  "residential-investment": "Residential / Investment",
  "vehicle-asset": "Vehicle & Asset Finance",
  "business-loans": "Business Loans",
};

const referralLabels: Record<string, string> = {
  google: "Google Search",
  facebook: "Facebook",
  instagram: "Instagram",
  "friend-family": "Friend or Family",
  "real-estate-agent": "Real Estate Agent",
  "existing-client": "Existing Client",
  signage: "Signage / Banner",
  other: "Other",
};

function PercentChange({ current, previous }: { current: number; previous: number }) {
  if (previous === 0 && current === 0) return null;
  if (previous === 0) return <span className="text-xs text-green-600 font-medium">New</span>;
  const pct = Math.round(((current - previous) / previous) * 100);
  if (pct === 0) return <span className="text-xs text-muted-foreground">No change</span>;
  return (
    <span className={cn("inline-flex items-center gap-0.5 text-xs font-medium", pct > 0 ? "text-green-600" : "text-red-500")}>
      {pct > 0 ? <FiArrowUp className="size-3" /> : <FiArrowDown className="size-3" />}
      {Math.abs(pct)}%
    </span>
  );
}

function BarChart({ data, labelMap }: { data: Record<string, number>; labelMap?: Record<string, string> }) {
  const entries = Object.entries(data).sort((a, b) => b[1] - a[1]);
  const max = Math.max(...entries.map(([, v]) => v), 1);

  if (entries.length === 0) {
    return <p className="text-sm text-muted-foreground py-4 text-center">No data yet</p>;
  }

  return (
    <div className="space-y-3">
      {entries.map(([key, value]) => (
        <div key={key}>
          <div className="flex items-center justify-between text-sm mb-1">
            <span className="text-muted-foreground truncate mr-2">
              {labelMap?.[key] || key}
            </span>
            <span className="font-medium tabular-nums">{value}</span>
          </div>
          <div className="h-2 bg-muted rounded-full overflow-hidden">
            <div
              className="h-full bg-primary rounded-full transition-all duration-500"
              style={{ width: `${(value / max) * 100}%` }}
            />
          </div>
        </div>
      ))}
    </div>
  );
}

function MonthlyChart({ data }: { data: { month: string; count: number }[] }) {
  const max = Math.max(...data.map((d) => d.count), 1);

  if (data.length === 0) {
    return <p className="text-sm text-muted-foreground py-4 text-center">No data yet</p>;
  }

  return (
    <div className="flex items-end gap-2 h-40">
      {data.map((item) => (
        <div key={item.month} className="flex-1 flex flex-col items-center gap-1">
          <span className="text-xs font-medium tabular-nums">{item.count}</span>
          <div className="w-full bg-muted rounded-t-sm overflow-hidden flex-1 flex items-end">
            <div
              className="w-full bg-primary/80 rounded-t-sm transition-all duration-500"
              style={{ height: `${(item.count / max) * 100}%`, minHeight: item.count > 0 ? "4px" : "0" }}
            />
          </div>
          <span className="text-[10px] text-muted-foreground">{item.month}</span>
        </div>
      ))}
    </div>
  );
}

function DailyChart({ data }: { data: { date: string; sessions: number; users: number }[] }) {
  const max = Math.max(...data.map((d) => d.sessions), 1);

  if (data.length === 0) {
    return <p className="text-sm text-muted-foreground py-4 text-center">No data yet</p>;
  }

  // Show every 5th label to avoid crowding
  return (
    <div className="flex items-end gap-[3px] h-40">
      {data.map((item, i) => (
        <div key={item.date} className="flex-1 flex flex-col items-center gap-1 min-w-0">
          <div className="w-full bg-muted rounded-t-sm overflow-hidden flex-1 flex items-end">
            <div
              className="w-full bg-blue-500/80 rounded-t-sm transition-all duration-500"
              style={{ height: `${(item.sessions / max) * 100}%`, minHeight: item.sessions > 0 ? "2px" : "0" }}
            />
          </div>
          {i % 7 === 0 && (
            <span className="text-[9px] text-muted-foreground">{item.date}</span>
          )}
        </div>
      ))}
    </div>
  );
}

function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
}

export function AnalyticsPage() {
  const [data, setData] = useState<AnalyticsData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState<"website" | "search" | "leads" | "campaigns" | "blog">("website");

  const fetchAnalytics = useCallback(async (showRefresh = false) => {
    if (showRefresh) setIsRefreshing(true);
    else setIsLoading(true);

    try {
      const response = await fetch("/api/admin/analytics", {
        credentials: "include",
      });
      if (response.ok) {
        const result = await response.json() as AnalyticsData;
        setData(result);
      }
    } catch (error) {
      console.error("Failed to fetch analytics:", error);
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  const ga4 = data?.ga4;
  const gsc = data?.searchConsole;

  const tabs = [
    { id: "website" as const, label: "Website Traffic" },
    { id: "search" as const, label: "Search & SEO" },
    { id: "leads" as const, label: "Leads & CRM" },
    { id: "campaigns" as const, label: "Email Campaigns" },
    { id: "blog" as const, label: "Blog" },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-serif text-3xl font-medium text-slate-900">
            Analytics
          </h1>
          <p className="text-muted-foreground mt-1">
            Website performance and lead data
            {data?.lastUpdated && (
              <span className="text-xs ml-2">
                &middot; Updated {new Date(data.lastUpdated).toLocaleTimeString()}
              </span>
            )}
          </p>
        </div>
        <Button
          variant="outline"
          className="btn-pill gap-2"
          onClick={() => fetchAnalytics(true)}
          disabled={isRefreshing}
        >
          <FiRefreshCw className={cn("size-4", isRefreshing && "animate-spin")} />
          {isRefreshing ? "Refreshing..." : "Pull Live Data"}
        </Button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-muted p-1 rounded-xl w-fit">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={cn(
              "px-4 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer",
              activeTab === tab.id
                ? "bg-white text-foreground shadow-sm"
                : "text-muted-foreground hover:text-foreground"
            )}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* ================= WEBSITE TRAFFIC TAB ================= */}
      {activeTab === "website" && (
        <>
          {!ga4 && !isLoading ? (
            <Card>
              <CardContent className="py-12 text-center">
                <FiGlobe className="size-12 mx-auto text-muted-foreground/30 mb-4" />
                <h3 className="font-medium text-lg mb-2">Google Analytics not connected</h3>
                <p className="text-sm text-muted-foreground max-w-md mx-auto mb-4">
                  To see website traffic data, connect your Google Analytics 4 property by adding these environment variables to Cloudflare:
                </p>
                <div className="bg-muted rounded-xl p-4 max-w-lg mx-auto text-left">
                  <code className="text-xs space-y-1 block">
                    <span className="block"><span className="text-primary font-medium">GA4_PROPERTY_ID</span> = your GA4 property ID</span>
                    <span className="block"><span className="text-primary font-medium">GA4_CLIENT_EMAIL</span> = service account email</span>
                    <span className="block"><span className="text-primary font-medium">GA4_PRIVATE_KEY</span> = service account private key</span>
                  </code>
                </div>
                <p className="text-xs text-muted-foreground mt-4">
                  Requires a Google Cloud service account with Viewer access to your GA4 property.
                </p>
              </CardContent>
            </Card>
          ) : isLoading ? (
            <div className="space-y-6">
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                {Array.from({ length: 4 }).map((_, i) => <CardSkeleton key={i} />)}
              </div>
              <CardSkeleton />
            </div>
          ) : ga4 && (
            <>
              {/* GA4 Overview Stats */}
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <div className="p-3 rounded-full bg-blue-100 text-blue-600">
                        <FiUsers className="w-5 h-5" />
                      </div>
                      <PercentChange current={ga4.overview.totalUsers} previous={ga4.overviewPrevious.totalUsers} />
                    </div>
                    <p className="text-3xl font-semibold">{ga4.overview.totalUsers.toLocaleString()}</p>
                    <p className="text-sm text-muted-foreground">Total Users</p>
                    <p className="text-xs text-muted-foreground mt-1">Last 30 days</p>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <div className="p-3 rounded-full bg-green-100 text-green-600">
                        <FiEye className="w-5 h-5" />
                      </div>
                      <PercentChange current={ga4.overview.pageviews} previous={ga4.overviewPrevious.pageviews} />
                    </div>
                    <p className="text-3xl font-semibold">{ga4.overview.pageviews.toLocaleString()}</p>
                    <p className="text-sm text-muted-foreground">Page Views</p>
                    <p className="text-xs text-muted-foreground mt-1">Last 30 days</p>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <div className="p-3 rounded-full bg-purple-100 text-purple-600">
                        <FiTrendingUp className="w-5 h-5" />
                      </div>
                      <PercentChange current={ga4.overview.sessions} previous={ga4.overviewPrevious.sessions} />
                    </div>
                    <p className="text-3xl font-semibold">{ga4.overview.sessions.toLocaleString()}</p>
                    <p className="text-sm text-muted-foreground">Sessions</p>
                    <p className="text-xs text-muted-foreground mt-1">Avg {formatDuration(ga4.overview.avgSessionDuration)}</p>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <div className="p-3 rounded-full bg-amber-100 text-amber-600">
                        <FiGlobe className="w-5 h-5" />
                      </div>
                    </div>
                    <p className="text-3xl font-semibold">{ga4.overview.bounceRate}%</p>
                    <p className="text-sm text-muted-foreground">Bounce Rate</p>
                    <p className="text-xs text-muted-foreground mt-1">{ga4.overview.newUsers.toLocaleString()} new users</p>
                  </CardContent>
                </Card>
              </div>

              {/* Active Users (Realtime) */}
              {ga4.realtime.activeUsers > 0 && (
                <Card className="border-green-200 bg-green-50/50">
                  <CardContent className="p-4 flex items-center gap-3">
                    <div className="size-3 bg-green-500 rounded-full animate-pulse" />
                    <span className="font-medium">{ga4.realtime.activeUsers} active user{ga4.realtime.activeUsers !== 1 ? "s" : ""} right now</span>
                  </CardContent>
                </Card>
              )}

              {/* Daily Sessions Chart */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FiBarChart2 className="size-5" />
                    Daily Sessions (Last 30 Days)
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <DailyChart data={ga4.dailySessions} />
                </CardContent>
              </Card>

              {/* GA4 Breakdown Charts */}
              <div className="grid gap-6 md:grid-cols-2">
                {/* Top Pages */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <FiEye className="size-5" />
                      Top Pages
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {ga4.topPages.length === 0 ? (
                      <p className="text-sm text-muted-foreground py-4 text-center">No data yet</p>
                    ) : (
                      <div className="space-y-3">
                        {ga4.topPages.map((page) => (
                          <div key={page.page}>
                            <div className="flex items-center justify-between text-sm mb-1">
                              <span className="text-muted-foreground truncate mr-2 font-mono text-xs">
                                {page.page}
                              </span>
                              <span className="font-medium tabular-nums shrink-0">{page.views.toLocaleString()}</span>
                            </div>
                            <div className="h-2 bg-muted rounded-full overflow-hidden">
                              <div
                                className="h-full bg-blue-500 rounded-full transition-all duration-500"
                                style={{ width: `${(page.views / (ga4.topPages[0]?.views || 1)) * 100}%` }}
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Traffic Sources */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <FiTrendingUp className="size-5" />
                      Traffic Sources
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {ga4.trafficSources.length === 0 ? (
                      <p className="text-sm text-muted-foreground py-4 text-center">No data yet</p>
                    ) : (
                      <div className="space-y-3">
                        {ga4.trafficSources.map((src) => (
                          <div key={src.source}>
                            <div className="flex items-center justify-between text-sm mb-1">
                              <span className="text-muted-foreground truncate mr-2">{src.source}</span>
                              <span className="font-medium tabular-nums shrink-0">{src.users.toLocaleString()} users</span>
                            </div>
                            <div className="h-2 bg-muted rounded-full overflow-hidden">
                              <div
                                className="h-full bg-green-500 rounded-full transition-all duration-500"
                                style={{ width: `${(src.users / (ga4.trafficSources[0]?.users || 1)) * 100}%` }}
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Device Breakdown */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <FiMonitor className="size-5" />
                      Devices
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {ga4.deviceBreakdown.length === 0 ? (
                      <p className="text-sm text-muted-foreground py-4 text-center">No data yet</p>
                    ) : (
                      <div className="space-y-4">
                        {ga4.deviceBreakdown.map((d) => {
                          const total = ga4.deviceBreakdown.reduce((sum, x) => sum + x.users, 0);
                          const pct = total > 0 ? Math.round((d.users / total) * 100) : 0;
                          return (
                            <div key={d.device} className="flex items-center gap-4">
                              <div className="flex-1">
                                <div className="flex items-center justify-between text-sm mb-1">
                                  <span className="capitalize">{d.device}</span>
                                  <span className="text-muted-foreground">{pct}%</span>
                                </div>
                                <div className="h-2 bg-muted rounded-full overflow-hidden">
                                  <div
                                    className="h-full bg-purple-500 rounded-full transition-all duration-500"
                                    style={{ width: `${pct}%` }}
                                  />
                                </div>
                              </div>
                              <span className="font-medium tabular-nums text-sm w-16 text-right">{d.users.toLocaleString()}</span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Top Countries */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <FiGlobe className="size-5" />
                      Top Countries
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {ga4.topCountries.length === 0 ? (
                      <p className="text-sm text-muted-foreground py-4 text-center">No data yet</p>
                    ) : (
                      <div className="space-y-3">
                        {ga4.topCountries.map((c) => (
                          <div key={c.country}>
                            <div className="flex items-center justify-between text-sm mb-1">
                              <span className="text-muted-foreground">{c.country}</span>
                              <span className="font-medium tabular-nums">{c.users.toLocaleString()}</span>
                            </div>
                            <div className="h-2 bg-muted rounded-full overflow-hidden">
                              <div
                                className="h-full bg-amber-500 rounded-full transition-all duration-500"
                                style={{ width: `${(c.users / (ga4.topCountries[0]?.users || 1)) * 100}%` }}
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </>
          )}
        </>
      )}

      {/* ================= SEARCH & SEO TAB ================= */}
      {activeTab === "search" && (
        <>
          {!gsc && !isLoading ? (
            <Card>
              <CardContent className="py-12 text-center">
                <FiSearch className="size-12 mx-auto text-muted-foreground/30 mb-4" />
                <h3 className="font-medium text-lg mb-2">Google Search Console not connected</h3>
                <p className="text-sm text-muted-foreground max-w-md mx-auto mb-4">
                  To see search keyword data, connect Google Search Console by adding the service account and setting the environment variable:
                </p>
                <div className="bg-muted rounded-xl p-4 max-w-lg mx-auto text-left">
                  <code className="text-xs space-y-1 block">
                    <span className="block"><span className="text-primary font-medium">GSC_SITE_URL</span> = https://pathfinancial.com.au</span>
                  </code>
                </div>
                <p className="text-xs text-muted-foreground mt-4">
                  Also add the service account email as a user in Search Console (Settings &gt; Users and permissions).
                </p>
              </CardContent>
            </Card>
          ) : isLoading ? (
            <div className="space-y-6">
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                {Array.from({ length: 4 }).map((_, i) => <CardSkeleton key={i} />)}
              </div>
              <CardSkeleton />
            </div>
          ) : gsc && (
            <>
              {/* Search Console Overview */}
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <div className="p-3 rounded-full bg-blue-100 text-blue-600">
                        <FiTarget className="w-5 h-5" />
                      </div>
                    </div>
                    <p className="text-3xl font-semibold">{gsc.overview.totalClicks.toLocaleString()}</p>
                    <p className="text-sm text-muted-foreground">Total Clicks</p>
                    <p className="text-xs text-muted-foreground mt-1">Last 30 days from Google</p>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <div className="p-3 rounded-full bg-purple-100 text-purple-600">
                        <FiEye className="w-5 h-5" />
                      </div>
                    </div>
                    <p className="text-3xl font-semibold">{gsc.overview.totalImpressions.toLocaleString()}</p>
                    <p className="text-sm text-muted-foreground">Impressions</p>
                    <p className="text-xs text-muted-foreground mt-1">Times shown in search results</p>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <div className="p-3 rounded-full bg-green-100 text-green-600">
                        <FiTrendingUp className="w-5 h-5" />
                      </div>
                    </div>
                    <p className="text-3xl font-semibold">{gsc.overview.avgCTR}%</p>
                    <p className="text-sm text-muted-foreground">Click-Through Rate</p>
                    <p className="text-xs text-muted-foreground mt-1">Clicks / Impressions</p>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <div className="p-3 rounded-full bg-amber-100 text-amber-600">
                        <FiBarChart2 className="w-5 h-5" />
                      </div>
                    </div>
                    <p className="text-3xl font-semibold">{gsc.overview.avgPosition}</p>
                    <p className="text-sm text-muted-foreground">Avg Position</p>
                    <p className="text-xs text-muted-foreground mt-1">In Google search results</p>
                  </CardContent>
                </Card>
              </div>

              {/* Daily Clicks & Impressions Chart */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FiBarChart2 className="size-5" />
                    Search Performance (Last 30 Days)
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {gsc.dailyData.length === 0 ? (
                    <p className="text-sm text-muted-foreground py-4 text-center">No data yet</p>
                  ) : (
                    <div className="space-y-4">
                      <div>
                        <p className="text-xs font-medium text-muted-foreground mb-2">Clicks</p>
                        <div className="flex items-end gap-[3px] h-24">
                          {gsc.dailyData.map((item, i) => {
                            const maxClicks = Math.max(...gsc.dailyData.map((d) => d.clicks), 1);
                            return (
                              <div key={item.date} className="flex-1 flex flex-col items-center gap-1 min-w-0">
                                <div className="w-full bg-muted rounded-t-sm overflow-hidden flex-1 flex items-end">
                                  <div
                                    className="w-full bg-blue-500/80 rounded-t-sm"
                                    style={{ height: `${(item.clicks / maxClicks) * 100}%`, minHeight: item.clicks > 0 ? "2px" : "0" }}
                                  />
                                </div>
                                {i % 7 === 0 && (
                                  <span className="text-[9px] text-muted-foreground">{item.date.slice(5)}</span>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                      <div>
                        <p className="text-xs font-medium text-muted-foreground mb-2">Impressions</p>
                        <div className="flex items-end gap-[3px] h-24">
                          {gsc.dailyData.map((item, i) => {
                            const maxImpressions = Math.max(...gsc.dailyData.map((d) => d.impressions), 1);
                            return (
                              <div key={item.date} className="flex-1 flex flex-col items-center gap-1 min-w-0">
                                <div className="w-full bg-muted rounded-t-sm overflow-hidden flex-1 flex items-end">
                                  <div
                                    className="w-full bg-purple-500/80 rounded-t-sm"
                                    style={{ height: `${(item.impressions / maxImpressions) * 100}%`, minHeight: item.impressions > 0 ? "2px" : "0" }}
                                  />
                                </div>
                                {i % 7 === 0 && (
                                  <span className="text-[9px] text-muted-foreground">{item.date.slice(5)}</span>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Top Queries & Top Pages */}
              <div className="grid gap-6 md:grid-cols-2">
                {/* Top Search Queries */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <FiSearch className="size-5" />
                      Top Search Keywords
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {gsc.topQueries.length === 0 ? (
                      <p className="text-sm text-muted-foreground py-4 text-center">No data yet</p>
                    ) : (
                      <div className="space-y-1">
                        {/* Header */}
                        <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-muted-foreground font-medium pb-2 border-b">
                          <span className="flex-1">Keyword</span>
                          <span className="w-14 text-right">Clicks</span>
                          <span className="w-14 text-right">Impr.</span>
                          <span className="w-12 text-right">CTR</span>
                          <span className="w-10 text-right">Pos.</span>
                        </div>
                        {gsc.topQueries.map((q) => (
                          <div
                            key={q.query}
                            className="flex items-center gap-2 py-2 text-sm border-b border-border/30 last:border-0"
                          >
                            <span className="flex-1 truncate text-muted-foreground">{q.query}</span>
                            <span className="w-14 text-right font-medium tabular-nums">{q.clicks}</span>
                            <span className="w-14 text-right tabular-nums text-muted-foreground">{q.impressions.toLocaleString()}</span>
                            <span className="w-12 text-right tabular-nums text-muted-foreground">{q.ctr}%</span>
                            <span className={cn(
                              "w-10 text-right tabular-nums font-medium",
                              q.position <= 3 ? "text-green-600" : q.position <= 10 ? "text-amber-600" : "text-muted-foreground"
                            )}>
                              {q.position}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Top Pages in Search */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <FiGlobe className="size-5" />
                      Top Pages in Search
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {gsc.topPages.length === 0 ? (
                      <p className="text-sm text-muted-foreground py-4 text-center">No data yet</p>
                    ) : (
                      <div className="space-y-1">
                        <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-muted-foreground font-medium pb-2 border-b">
                          <span className="flex-1">Page</span>
                          <span className="w-14 text-right">Clicks</span>
                          <span className="w-14 text-right">Impr.</span>
                          <span className="w-10 text-right">Pos.</span>
                        </div>
                        {gsc.topPages.map((p) => (
                          <div
                            key={p.page}
                            className="flex items-center gap-2 py-2 text-sm border-b border-border/30 last:border-0"
                          >
                            <span className="flex-1 truncate text-muted-foreground font-mono text-xs">{p.page}</span>
                            <span className="w-14 text-right font-medium tabular-nums">{p.clicks}</span>
                            <span className="w-14 text-right tabular-nums text-muted-foreground">{p.impressions.toLocaleString()}</span>
                            <span className={cn(
                              "w-10 text-right tabular-nums font-medium",
                              p.position <= 3 ? "text-green-600" : p.position <= 10 ? "text-amber-600" : "text-muted-foreground"
                            )}>
                              {p.position}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </>
          )}
        </>
      )}

      {/* ================= LEADS & CRM TAB ================= */}
      {activeTab === "leads" && (
        <>
          {/* Overview Stats */}
          {isLoading ? (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
              {Array.from({ length: 4 }).map((_, i) => <CardSkeleton key={i} />)}
            </div>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="p-3 rounded-full bg-amber-100 text-amber-600">
                      <FiInbox className="w-5 h-5" />
                    </div>
                    <PercentChange current={data?.overview.leadsThisMonth || 0} previous={data?.overview.leadsLastMonth || 0} />
                  </div>
                  <p className="text-3xl font-semibold">{data?.overview.totalLeads || 0}</p>
                  <p className="text-sm text-muted-foreground">Total Leads</p>
                  <p className="text-xs text-muted-foreground mt-1">{data?.overview.leadsThisMonth || 0} this month</p>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="p-3 rounded-full bg-blue-100 text-blue-600">
                      <FiUsers className="w-5 h-5" />
                    </div>
                  </div>
                  <p className="text-3xl font-semibold">{data?.overview.totalCustomers || 0}</p>
                  <p className="text-sm text-muted-foreground">Total Customers</p>
                  <p className="text-xs text-muted-foreground mt-1">{data?.overview.customersThisMonth || 0} this month</p>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="p-3 rounded-full bg-purple-100 text-purple-600">
                      <FiMail className="w-5 h-5" />
                    </div>
                  </div>
                  <p className="text-3xl font-semibold">{data?.overview.totalSubscribers || 0}</p>
                  <p className="text-sm text-muted-foreground">Subscribers</p>
                  <p className="text-xs text-muted-foreground mt-1">{data?.overview.subscribersThisMonth || 0} this month</p>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="p-3 rounded-full bg-green-100 text-green-600">
                      <FiTrendingUp className="w-5 h-5" />
                    </div>
                  </div>
                  <p className="text-3xl font-semibold">{data?.overview.conversionRate || 0}%</p>
                  <p className="text-sm text-muted-foreground">Conversion Rate</p>
                  <p className="text-xs text-muted-foreground mt-1">Leads → Converted</p>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Monthly Leads Chart */}
          {isLoading ? (
            <CardSkeleton />
          ) : (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FiBarChart2 className="size-5" />
                  Leads Over Time
                </CardTitle>
              </CardHeader>
              <CardContent>
                <MonthlyChart data={data?.leadsByMonth || []} />
              </CardContent>
            </Card>
          )}

          {/* Breakdown Charts */}
          {isLoading ? (
            <div className="grid gap-6 md:grid-cols-2">
              <CardSkeleton />
              <CardSkeleton />
            </div>
          ) : (
            <div className="grid gap-6 md:grid-cols-2">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FiPieChart className="size-5" />
                    Lead Pipeline
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2 mb-4">
                    {Object.entries(data?.leadsByStatus || {}).map(([status, count]) => (
                      <Badge key={status} variant="secondary" className={statusColors[status] || ""}>
                        {statusLabels[status] || status}: {count}
                      </Badge>
                    ))}
                  </div>
                  <BarChart data={data?.leadsByStatus || {}} labelMap={statusLabels} />
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FiTrendingUp className="size-5" />
                    Lead Sources
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <BarChart data={data?.leadsBySource || {}} labelMap={sourceLabels} />
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FiBarChart2 className="size-5" />
                    Services Interest
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <BarChart data={data?.leadsByService || {}} labelMap={serviceLabels} />
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FiUsers className="size-5" />
                    How They Found Us
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <BarChart data={data?.leadsByReferral || {}} labelMap={referralLabels} />
                </CardContent>
              </Card>
            </div>
          )}
        </>
      )}

      {/* ================= EMAIL CAMPAIGNS TAB ================= */}
      {activeTab === "campaigns" && (
        <>
          {isLoading ? (
            <CardSkeleton />
          ) : (data?.campaigns.totalSent || 0) === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <FiMail className="size-12 mx-auto text-muted-foreground/30 mb-4" />
                <h3 className="font-medium text-lg mb-2">No campaigns sent yet</h3>
                <p className="text-sm text-muted-foreground">
                  Campaign performance data will appear here once you send your first email campaign.
                </p>
              </CardContent>
            </Card>
          ) : (
            <>
              {/* Campaign Overview Stats */}
              <div className="grid gap-4 md:grid-cols-3">
                <Card>
                  <CardContent className="p-6 text-center">
                    <p className="text-3xl font-semibold">{data?.campaigns.totalSent.toLocaleString()}</p>
                    <p className="text-sm text-muted-foreground mt-1">Emails Sent</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-6 text-center">
                    <p className="text-3xl font-semibold">{data?.campaigns.openRate}%</p>
                    <p className="text-sm text-muted-foreground mt-1">Open Rate</p>
                    <p className="text-xs text-muted-foreground">{data?.campaigns.totalOpened.toLocaleString()} opens</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-6 text-center">
                    <p className="text-3xl font-semibold">{data?.campaigns.clickRate}%</p>
                    <p className="text-sm text-muted-foreground mt-1">Click Rate</p>
                    <p className="text-xs text-muted-foreground">{data?.campaigns.totalClicked.toLocaleString()} clicks</p>
                  </CardContent>
                </Card>
              </div>

              {/* Recent Campaigns */}
              {(data?.campaigns.recentCampaigns?.length || 0) > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle>Recent Campaigns</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {data?.campaigns.recentCampaigns.map((campaign) => {
                        const campOpenRate = campaign.totalSent > 0 ? Math.round((campaign.totalOpened / campaign.totalSent) * 100) : 0;
                        const campClickRate = campaign.totalSent > 0 ? Math.round((campaign.totalClicked / campaign.totalSent) * 100) : 0;
                        return (
                          <div
                            key={campaign.name + campaign.sentAt}
                            className="flex items-center justify-between p-4 rounded-lg bg-muted/50"
                          >
                            <div className="min-w-0">
                              <p className="font-medium text-sm truncate">{campaign.name}</p>
                              <p className="text-xs text-muted-foreground">
                                {new Date(campaign.sentAt).toLocaleDateString()}
                              </p>
                            </div>
                            <div className="flex items-center gap-4 text-xs">
                              <div className="text-center">
                                <p className="font-medium">{campaign.totalSent}</p>
                                <p className="text-muted-foreground">sent</p>
                              </div>
                              <div className="text-center">
                                <p className="font-medium">{campOpenRate}%</p>
                                <p className="text-muted-foreground">opens</p>
                              </div>
                              <div className="text-center">
                                <p className="font-medium">{campClickRate}%</p>
                                <p className="text-muted-foreground">clicks</p>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </>
      )}

      {/* ================= BLOG TAB ================= */}
      {activeTab === "blog" && (
        <>
          {!ga4 && !isLoading ? (
            <Card>
              <CardContent className="py-12 text-center">
                <FiFileText className="size-12 mx-auto text-muted-foreground/30 mb-4" />
                <h3 className="font-medium text-lg mb-2">Blog Analytics</h3>
                <p className="text-sm text-muted-foreground max-w-md mx-auto">
                  Connect Google Analytics 4 to see detailed blog performance metrics.
                  Blog page views are filtered from your GA4 top pages data.
                </p>
              </CardContent>
            </Card>
          ) : isLoading ? (
            <div className="space-y-6">
              <div className="grid gap-4 md:grid-cols-3">
                {Array.from({ length: 3 }).map((_, i) => <CardSkeleton key={i} />)}
              </div>
              <CardSkeleton />
            </div>
          ) : ga4 && (() => {
            const blogPages = ga4.topPages.filter(p => p.page.includes("/blog"));
            const totalBlogViews = blogPages.reduce((sum, p) => sum + p.views, 0);
            const totalBlogUsers = blogPages.reduce((sum, p) => sum + p.users, 0);
            const totalSiteViews = ga4.overview.pageviews;
            const blogSharePct = totalSiteViews > 0 ? Math.round((totalBlogViews / totalSiteViews) * 100) : 0;

            return (
              <>
                {/* Blog Overview Stats */}
                <div className="grid gap-4 md:grid-cols-3">
                  <Card>
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between mb-4">
                        <div className="p-3 rounded-full bg-purple-100 text-purple-600">
                          <FiEye className="w-5 h-5" />
                        </div>
                      </div>
                      <p className="text-3xl font-semibold">{totalBlogViews.toLocaleString()}</p>
                      <p className="text-sm text-muted-foreground">Blog Page Views</p>
                      <p className="text-xs text-muted-foreground mt-1">Last 30 days</p>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between mb-4">
                        <div className="p-3 rounded-full bg-blue-100 text-blue-600">
                          <FiUsers className="w-5 h-5" />
                        </div>
                      </div>
                      <p className="text-3xl font-semibold">{totalBlogUsers.toLocaleString()}</p>
                      <p className="text-sm text-muted-foreground">Blog Readers</p>
                      <p className="text-xs text-muted-foreground mt-1">Unique users</p>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between mb-4">
                        <div className="p-3 rounded-full bg-green-100 text-green-600">
                          <FiPieChart className="w-5 h-5" />
                        </div>
                      </div>
                      <p className="text-3xl font-semibold">{blogSharePct}%</p>
                      <p className="text-sm text-muted-foreground">Share of Traffic</p>
                      <p className="text-xs text-muted-foreground mt-1">Blog vs total page views</p>
                    </CardContent>
                  </Card>
                </div>

                {/* Blog Posts Performance */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <FiFileText className="size-5" />
                      Blog Post Performance
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {blogPages.length === 0 ? (
                      <div className="text-center py-8">
                        <FiFileText className="size-10 mx-auto text-muted-foreground/30 mb-3" />
                        <p className="text-sm text-muted-foreground">No blog page views recorded yet</p>
                        <p className="text-xs text-muted-foreground mt-1">Blog views will appear here once visitors read your posts</p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {/* Table Header */}
                        <div className="grid grid-cols-[1fr_100px_100px] gap-4 text-xs font-semibold text-muted-foreground uppercase tracking-wider pb-2 border-b">
                          <span>Page</span>
                          <span className="text-right">Views</span>
                          <span className="text-right">Users</span>
                        </div>
                        {blogPages.map((page) => (
                          <div key={page.page} className="grid grid-cols-[1fr_100px_100px] gap-4 items-center">
                            <div>
                              <span className="text-sm font-medium truncate block">
                                {page.page.replace("/blog/", "").replace("/blog", "Blog Index").replace(/-/g, " ")}
                              </span>
                              <span className="text-xs text-muted-foreground font-mono">{page.page}</span>
                            </div>
                            <span className="text-sm font-semibold tabular-nums text-right">{page.views.toLocaleString()}</span>
                            <span className="text-sm text-muted-foreground tabular-nums text-right">{page.users.toLocaleString()}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Blog vs Site Traffic */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <FiBarChart2 className="size-5" />
                      Blog vs Site Traffic
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div>
                        <div className="flex items-center justify-between text-sm mb-1">
                          <span className="text-muted-foreground">Blog Pages</span>
                          <span className="font-medium tabular-nums">{totalBlogViews.toLocaleString()} views</span>
                        </div>
                        <div className="h-3 bg-muted rounded-full overflow-hidden">
                          <div
                            className="h-full bg-purple-500 rounded-full transition-all duration-500"
                            style={{ width: `${blogSharePct}%`, minWidth: totalBlogViews > 0 ? '4px' : '0' }}
                          />
                        </div>
                      </div>
                      <div>
                        <div className="flex items-center justify-between text-sm mb-1">
                          <span className="text-muted-foreground">Other Pages</span>
                          <span className="font-medium tabular-nums">{(totalSiteViews - totalBlogViews).toLocaleString()} views</span>
                        </div>
                        <div className="h-3 bg-muted rounded-full overflow-hidden">
                          <div
                            className="h-full bg-blue-500 rounded-full transition-all duration-500"
                            style={{ width: `${100 - blogSharePct}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </>
            );
          })()}
        </>
      )}
    </div>
  );
}
